(function () {
  'use strict';

  const documentLanguage = (document.documentElement.lang || '').toLowerCase();
  document.documentElement.dir = documentLanguage.startsWith('ar') ? 'rtl' : 'ltr';

  const controllers = new Map();
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function normalize(value) {
    return (value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function normalizeInternalStorefrontLinks(scope = document) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const localePrefix = routeRoot === '/' ? '' : routeRoot.replace(/\/$/, '');
    scope.querySelectorAll?.('a[href]').forEach((link) => {
      const rawHref = link.getAttribute('href') || '';
      if (!rawHref || rawHref.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(rawHref)) return;
      if (!/^https?:\/\//i.test(rawHref) && !rawHref.startsWith('/')) return;
      let url;
      try { url = new URL(rawHref, window.location.href); } catch (error) { return; }
      const ownStorefront = url.hostname === window.location.hostname || /(^|\.)bluminkaktus\.com$/i.test(url.hostname);
      if (!ownStorefront || url.pathname.startsWith('/customer_authentication/')) return;
      const storefrontPath = localePrefix && url.pathname.startsWith(`${localePrefix}/`)
        ? url.pathname.slice(localePrefix.length)
        : url.pathname;
      if (!/^\/(pages|products|collections|policies|search|cart|blogs)(\/|$)/.test(storefrontPath)) return;
      const localizedPath = localePrefix ? `${localePrefix}${storefrontPath}` : storefrontPath;
      link.setAttribute('href', `${localizedPath}${url.search}${url.hash}`);
    });
  }

  class NavigationController {
    constructor(header) {
      this.header = header;
      this.section = header.closest('.section-header-navigation');
      this.sectionId = header.dataset.sectionId || this.section?.id || 'header';
      this.bar = header.querySelector('.header-bar');
      this.left = header.querySelector('.left-cluster');
      this.logo = header.querySelector('.header-logo-center');
      this.right = header.querySelector('.right-cluster');
      this.menuButton = header.querySelector('.hamburger__summary');
      this.mobilePanel = this.section?.querySelector('.hamburger__panel');
      this.megaPanel = this.section?.querySelector('.bk-below-mega') || document.getElementById('bkBelowMega');
      this.concernPanel = this.section?.querySelector('[data-bk-concern-menu]');
      this.searchTrigger = header.querySelector('.qi-search');
      this.searchPopup = this.section?.querySelector('[data-search-popup]');
      this.searchBackdrop = document.querySelector('.background-overlay');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.resizeFrame = 0;
      this.closeTimer = 0;
      this.openTimer = 0;
      this.mobileOpen = false;
      this.megaOpen = false;
      this.concernOpen = false;
      this.searchOpen = false;
      this.concernCloseTimer = 0;
      this.mobileScrollRestoreTimer = 0;
      this.mobileScrollX = 0;
      this.mobileScrollY = 0;
      this.collapsed = false;
      this.returnFocus = null;
      this.searchReturnFocus = null;
      this.searchBackgroundState = [];
      this.searchFocusTimers = [];
      this.searchOverflow = '';
      this.temporarilyChangedHomeTop = false;
      this.pointerOpenState = new WeakMap();

      if (!this.section || !this.bar || !this.left || !this.logo || !this.right || !this.menuButton) return;

      this.section.classList.add('bk-v29-controller-ready');
      this.localizeMenuLabels();
      this.localizeCommonInterface();
      this.prepareMegaMenu();
      this.prepareConcernMenu();
      this.prepareMobileMenu();
      this.prepareSearch();
      this.bindGlobalBehavior();
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.bar);
      this.scheduleMeasure();
      document.fonts?.ready.then(() => this.scheduleMeasure()).catch(() => {});
    }

    listen(target, type, listener, options = {}) {
      target?.addEventListener(type, listener, { ...options, signal: this.signal });
    }

    localizeMenuLabels() {
      const arabic = documentLanguage.startsWith('ar');
      const labels = new Map([
        ['shop', arabic ? 'تسوّقي' : 'Shop'],
        ['تسوق', arabic ? 'تسوّقي' : 'Shop'],
        ['blumin blog', arabic ? 'مدونة بلومن' : 'Blumin Blog'],
        ['how to', arabic ? 'مدونة بلومن' : 'Blumin Blog'],
        ['دليل بلومن', arabic ? 'مدونة بلومن' : 'Blumin Blog'],
        ['مدونة بلومن', arabic ? 'مدونة بلومن' : 'Blumin Blog'],
        ['مدونة بلومين', arabic ? 'مدونة بلومن' : 'Blumin Blog'],
        ['shop by concern', arabic ? 'تسوّقي حسب الاحتياج' : 'Shop by Concern'],
        ['التسوق حسب الاحتياج', arabic ? 'تسوّقي حسب الاحتياج' : 'Shop by Concern'],
        ['locate us', arabic ? 'أماكننا' : 'Locate Us'],
        ['موقعنا', arabic ? 'أماكننا' : 'Locate Us'],
        ['about us', arabic ? 'عن بلومن كاكتس' : 'About Us'],
        ['قصتنا', arabic ? 'عن بلومن كاكتس' : 'About Us']
      ]);
      this.section.querySelectorAll('.list-menu--inline > li > a > .text, .hamburger__list > li > a > .text').forEach((text) => {
        const translation = labels.get(normalize(text.textContent));
        if (translation) text.textContent = translation;
      });
    }

    localizeCommonInterface() {
      if (!documentLanguage.startsWith('ar')) return;
      const labels = new Map([
        ['sign in', 'تسجيل الدخول'],
        ['search', 'بحث'],
        ['يبحث', 'بحث'],
        ['my wish list', 'قائمة الرغبات'],
        ['my wishlist', 'قائمة الرغبات'],
        ['قائمة أمنياتي', 'قائمة الرغبات'],
        ['wishlist', 'قائمة الرغبات'],
        ['cart', 'سلة التسوّق'],
        ['عربة التسوق', 'سلة التسوّق'],
        ['shop all', 'تسوقي الكل'],
        ['lip care', 'العناية بالشفاه'],
        ['العناية بالبشرة', 'العناية بالشفاه'],
        ['hair care', 'العناية بالشعر'],
        ['accessories', 'الإكسسوارات'],
        ['bundles', 'المجموعات'],
        ['bestsellers', 'الأكثر مبيعًا'],
        ['most relevant', 'الأكثر صلة'],
        ['create account', 'إنشاء حساب'],
        ['create an account', 'إنشاء حساب'],
        ['qty', 'الكمية'],
        ['view full details', 'عرض التفاصيل الكاملة'],
        ['trending', 'الأكثر بحثًا'],
        ['bundle', 'مجموعة'],
        ['lip gloss', 'جلوس شفاه'],
        ['lip oil', 'زيت شفاه'],
        ['bonnet', 'بونيه'],
        ['pouches', 'حقائب صغيرة'],
        ['scrunchies', 'ربطات شعر'],
        ['our story', 'قصتنا'],
        ['our values', 'قيمنا'],
        ['ingredients', 'المكونات'],
        ['popular bk picks', 'اختيارات بلومن كاكتس'],
        ['email', 'البريد الإلكتروني'],
        ['email address', 'البريد الإلكتروني'],
        ['password', 'كلمة المرور'],
        ['forgot your password?', 'هل نسيتِ كلمة المرور؟'],
        ['forgot your password', 'هل نسيتِ كلمة المرور؟'],
        ['reviews', 'التقييمات'],
        ['review', 'تقييم'],
        ['regular price', 'السعر العادي'],
        ['sale price', 'سعر التخفيض'],
        ['juicy bloom gloss duo — mega x blumin’kaktus exclusive', 'ديو Juicy Bloom Gloss — حصري من بلومن كاكتس × ميجا'],
        ['curl definition', 'تحديد التموجات'],
        ['daily glow', 'إشراقة يومية'],
        ['damage and dryness', 'التلف والجفاف'],
        ['frizz', 'الهيشان'],
        ['how to', 'مدونة بلومن'],
        ['blumin blog', 'مدونة بلومن'],
        ['مدونة بلومين', 'مدونة بلومن'],
        ['skip to content', 'انتقلي إلى المحتوى'],
        ['free shipping over egp 2,000', 'شحن مجاني للطلبات التي تتجاوز ٢٬٠٠٠ جنيه'],
        ['welcome to blumin’kaktus', 'مرحبًا بكِ في بلومن كاكتس'],
        ['thanks for subscribing', 'شكرًا لاشتراككِ!'],
        ['this email has been registered', 'هذا البريد الإلكتروني مسجّل بالفعل.'],
        ['choosing a selection results in a full page refresh', 'اختيار أحد الخيارات يؤدي إلى إعادة تحميل الصفحة بالكامل.'],
        ['please review this message before continuing', 'يرجى مراجعة هذه الرسالة قبل المتابعة']
      ]);
      document.querySelectorAll('a, button, label, span, strong, h1, h2, h3, p, option, dt').forEach((element) => {
        if (element.closest('[lang="en"]')) return;
        if (element.children.length) return;
        const translation = labels.get(normalize(element.textContent));
        if (translation) element.textContent = translation;
        const reviewCount = normalize(element.textContent).match(/^(\d+)\s+reviews?$/);
        if (reviewCount) element.textContent = `${reviewCount[1]} تقييمات`;
      });
      document.querySelectorAll('[aria-label]').forEach((element) => {
        if (element.closest('[lang="en"]')) return;
        const translation = labels.get(normalize(element.getAttribute('aria-label')));
        if (translation) element.setAttribute('aria-label', translation);
      });
      document.querySelectorAll('[alt]').forEach((element) => {
        if (element.closest('[lang="en"]')) return;
        const translation = labels.get(normalize(element.getAttribute('alt')));
        if (translation) element.setAttribute('alt', translation);
      });
      document.querySelectorAll('input[placeholder="Email"], input[aria-label="Email"]').forEach((input) => {
        input.placeholder = 'البريد الإلكتروني';
        if (normalize(input.getAttribute('aria-label')) === 'email') input.setAttribute('aria-label', 'البريد الإلكتروني');
      });
      document.querySelectorAll('input[type="password"]').forEach((input) => {
        if (normalize(input.getAttribute('placeholder')) === 'password') input.placeholder = 'كلمة المرور';
        if (normalize(input.getAttribute('aria-label')) === 'password') input.setAttribute('aria-label', 'كلمة المرور');
      });
    }

    prepareMobileMenu() {
      if (!this.mobilePanel) return;
      this.mobilePanel.setAttribute('aria-hidden', 'true');
      this.menuButton.setAttribute('aria-expanded', 'false');
      this.menuButton.removeAttribute('data-open');

      this.prepareMobileTabbedShop();
      this.listen(this.menuButton, 'click', () => (this.mobileOpen ? this.closeMobile() : this.openMobile()));
      this.listen(this.menuButton, 'keydown', (event) => {
        if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;
        event.preventDefault();
        this.mobileOpen ? this.closeMobile() : this.openMobile();
      });
      this.listen(this.mobilePanel, 'click', (event) => {
        if (event.target.closest('a[href]')) this.closeMobile({ restoreFocus: false });
      });
    }

    prepareSearch() {
      if (!this.searchTrigger || !this.searchPopup) return;
      // A viewport modal cannot live inside the sticky header's transformed containing block.
      document.body.appendChild(this.searchPopup);
      this.searchPopup.setAttribute('aria-hidden', 'true');
      this.searchTrigger.setAttribute('aria-haspopup', 'dialog');
      this.searchTrigger.setAttribute('aria-controls', this.searchPopup.id);
      this.searchTrigger.setAttribute('aria-expanded', 'false');
      this.listen(this.searchPopup, 'transitionend', (event) => {
        if (event.target === this.searchPopup && event.propertyName === 'transform' && this.searchOpen) {
          this.searchPopup.querySelector('input[type="search"]')?.focus({ preventScroll: true });
        }
      });

      const open = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openSearch();
      };
      this.listen(this.searchTrigger, 'click', open);
      this.listen(this.searchTrigger, 'keydown', (event) => {
        if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;
        open(event);
      });
      this.searchPopup.querySelectorAll('[data-search-close-popup], .header-search-close').forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeSearch();
        });
      });
      this.listen(this.searchBackdrop, 'click', () => {
        if (this.searchOpen) this.closeSearch();
      });
    }

    openSearch() {
      if (!this.searchPopup || !this.searchTrigger || this.searchOpen) return;
      this.closeMobile({ restoreFocus: false });
      this.closeMega();
      this.closeConcern();
      this.searchOpen = true;
      this.searchReturnFocus = document.activeElement || this.searchTrigger;
      this.searchPopup.setAttribute('aria-hidden', 'false');
      this.searchTrigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('open_search_desktop');
      this.searchOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      const background = [this.header, document.getElementById('MainContent'), document.querySelector('footer')].filter(Boolean);
      this.searchBackgroundState = background.map((element) => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden')
      }));
      this.searchBackgroundState.forEach(({ element }) => {
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
      });
      const focusSearchInput = () => {
        if (!this.searchOpen) return;
        const input = this.searchPopup.querySelector('input[type="search"]');
        input?.focus({ preventScroll: true });
      };
      requestAnimationFrame(focusSearchInput);
    }

    closeSearch({ restoreFocus = true } = {}) {
      if (!this.searchPopup || !this.searchOpen) return;
      this.searchOpen = false;
      document.body.classList.remove('open_search_desktop');
      this.searchPopup.setAttribute('aria-hidden', 'true');
      this.searchTrigger?.setAttribute('aria-expanded', 'false');
      this.searchFocusTimers.forEach((timer) => window.clearTimeout(timer));
      this.searchFocusTimers = [];
      document.documentElement.style.overflow = this.searchOverflow;
      this.searchBackgroundState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.searchBackgroundState = [];
      if (restoreFocus && this.searchReturnFocus && document.contains(this.searchReturnFocus)) {
        this.searchReturnFocus.focus({ preventScroll: true });
      }
      this.searchReturnFocus = null;
    }

    trapSearchFocus(event) {
      if (!this.searchPopup || !this.searchOpen) return;
      const focusables = Array.from(this.searchPopup.querySelectorAll(focusableSelector)).filter((item) => {
        if (item.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(item);
        return item.tabIndex >= 0 && item.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
      });
      if (!focusables.length) return;
      const activeIndex = focusables.indexOf(document.activeElement);
      const nextIndex = activeIndex < 0 ? 0 : (activeIndex + (event.shiftKey ? -1 : 1) + focusables.length) % focusables.length;
      event.preventDefault();
      focusables[nextIndex].focus({ preventScroll: true });
    }

    prepareMobileTabbedShop() {
      const nav = this.mobilePanel?.querySelector('.hamburger__nav');
      if (!nav || nav.querySelector('[data-bk-v295-mobile-shop]')) return;
      const shopSources = this.collectPanelSources(this.megaPanel, '.bk-tabs [role="tab"]', '.bk-panel');
      const concernSources = this.collectPanelSources(this.concernPanel, '[data-bk-concern-tab]', '[data-bk-concern-panel]');
      if (shopSources.length < 4) return;

      const originalPrimaryLinks = Array.from(nav.querySelectorAll('.hamburger__list > li > a[href]'));
      const originalActionLinks = Array.from(this.mobilePanel.querySelectorAll('.hamburger__actions a[href]'));
      const module = document.createElement('div');
      const tablist = document.createElement('div');
      const panelsWrap = document.createElement('div');
      const mobileSources = shopSources.slice(0, 5).map((source) => ({ ...source, kind: 'products' }));
      mobileSources.push({
        label: documentLanguage.startsWith('ar') ? 'الاحتياجات' : 'Concerns',
        href: this.mobilePanel.dataset.bkShopAllUrl,
        kind: 'concerns',
        concernSources
      });

      module.className = 'bk-v295-mobile-shop';
      module.dataset.bkV295MobileShop = '';
      tablist.className = 'bk-v295-mobile-tabs';
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', documentLanguage.startsWith('ar') ? 'فئات التسوق' : 'Shop categories');
      panelsWrap.className = 'bk-v295-mobile-panels';

      const tabs = [];
      const panels = [];
      mobileSources.forEach((source, index) => {
        const tab = document.createElement('button');
        const panel = document.createElement('section');
        const rows = document.createElement('div');
        const tabId = `BKV295MobileTab-${this.sectionId}-${index}`;
        const panelId = `BKV295MobilePanel-${this.sectionId}-${index}`;
        tab.type = 'button';
        tab.id = tabId;
        tab.className = 'bk-v295-mobile-tab';
        tab.textContent = source.label;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        panel.id = panelId;
        panel.className = 'bk-v295-mobile-panel';
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
        panel.inert = true;
        rows.className = 'bk-v295-mobile-rows';

        if (source.kind === 'concerns') {
          source.concernSources.forEach((concern) => {
            const card = concern.panel.querySelector('.bk-card')?.cloneNode(true);
            if (!card) return;
            card.querySelectorAll('.bk-card__rating-row, .bk-card__rating, .bk-card__price').forEach((node) => node.remove());
            card.querySelectorAll('a[href]').forEach((link) => { link.href = concern.href; });
            const title = card.querySelector('.bk-title');
            if (title) {
              title.textContent = concern.label;
              title.title = concern.label;
            }
            const eyebrow = card.querySelector('.bk-card__eyebrow') || document.createElement('span');
            eyebrow.className = 'bk-card__eyebrow';
            eyebrow.textContent = documentLanguage.startsWith('ar') ? 'تسوّقي حسب الاحتياج' : 'Shop by concern';
            if (!eyebrow.parentElement) card.querySelector('.bk-card__link')?.insertBefore(eyebrow, title);
            this.prepareMobileRow(card);
            rows.appendChild(card);
          });
        } else {
          Array.from(source.panel.querySelectorAll('.bk-card')).slice(0, 6).forEach((sourceCard) => {
            const card = sourceCard.cloneNode(true);
            card.querySelectorAll('.bk-card__rating-row, .bk-card__rating').forEach((node) => node.remove());
            this.prepareMobileRow(card);
            rows.appendChild(card);
          });
        }

        const action = document.createElement('a');
        action.className = 'bk-v295-mobile-action';
        action.href = source.href;
        action.textContent = source.kind === 'concerns'
          ? (documentLanguage.startsWith('ar') ? 'تسوّقي حسب الاحتياج' : 'Shop by Concern')
          : (documentLanguage.startsWith('ar') ? `تسوقي كل ${source.label}` : `Shop all ${source.label}`);
        panel.append(rows, action);
        tablist.appendChild(tab);
        panelsWrap.appendChild(panel);
        tabs.push(tab);
        panels.push(panel);
        this.listen(tab, 'click', () => this.activateMobileTab(tab, tabs, panels));
        this.listen(tab, 'keydown', (event) => this.handleMobileTabKeydown(event, tab, tabs, panels, index));
      });

      module.append(tablist, panelsWrap);
      const secondary = document.createElement('div');
      secondary.className = 'bk-v295-mobile-secondary';
      const explore = document.createElement('nav');
      const utilities = document.createElement('nav');
      explore.className = 'bk-v296-mobile-explore';
      utilities.className = 'bk-v296-mobile-utilities';
      explore.setAttribute('aria-label', documentLanguage.startsWith('ar') ? 'استكشفي' : 'Explore');
      utilities.setAttribute('aria-label', documentLanguage.startsWith('ar') ? 'أدوات الحساب والتسوق' : 'Shopping utilities');
      const shopAll = document.createElement('a');
      const routeRoot = window.Shopify?.routes?.root || '/';
      const localizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
      shopAll.href = this.mobilePanel.dataset.bkShopAllUrl || `${localizedRoot}collections/all`;
      shopAll.textContent = documentLanguage.startsWith('ar') ? 'عرض جميع المنتجات' : 'Shop All';
      explore.appendChild(shopAll);
      ['how to', 'دليل بلومن', 'blumin blog', 'مدونة بلومن', 'locate us', 'أماكن تواجدنا', 'أماكننا', 'about us', 'من نحن', 'عن بلومن كاكتس'].forEach((label) => {
        const original = originalPrimaryLinks.find((link) => normalize(link.textContent) === label);
        if (original && !explore.querySelector(`a[href="${CSS.escape(original.getAttribute('href'))}"]`)) explore.appendChild(original.cloneNode(true));
      });
      const utilityRank = (link) => {
        const href = link.getAttribute('href') || '';
        if (href.includes('/search')) return 0;
        if (href.includes('customer_authentication') || href.includes('/account')) return 1;
        if (href.includes('wishlist')) return 2;
        if (href.includes('/cart')) return 3;
        return 4;
      };
      originalActionLinks.sort((a, b) => utilityRank(a) - utilityRank(b)).forEach((link) => utilities.appendChild(link.cloneNode(true)));
      // Preserve the real Shopify locale form when replacing the legacy mobile utilities.
      const localeForm = this.mobilePanel.querySelector('.hamburger__actions .bk-language-form');
      if (localeForm) {
        const mobileLocale = localeForm.cloneNode(true);
        mobileLocale.id = `${localeForm.id}-enhanced`;
        utilities.prepend(mobileLocale);
      }
      secondary.append(explore, utilities);
      nav.replaceChildren(module, secondary);
      this.mobilePanel.querySelector('.hamburger__divider')?.setAttribute('hidden', '');
      this.mobilePanel.querySelector('.hamburger__actions')?.setAttribute('hidden', '');
      this.mobilePanel.classList.add('bk-v295-mobile-ready');
      this.mobileTabs = tabs;
      this.mobileTabPanels = panels;
      this.activateMobileTab(tabs[0], tabs, panels, false);
      normalizeInternalStorefrontLinks(this.mobilePanel);
    }

    prepareMobileRow(card) {
      card.classList.add('bk-v295-mobile-row');
      this.deferPanelImages(card);
    }

    collectPanelSources(root, tabSelector, panelSelector) {
      if (!root) return [];
      return Array.from(root.querySelectorAll(tabSelector)).map((tab) => {
        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? root.querySelector(`#${CSS.escape(panelId)}`) : null;
        const viewAll = panel?.querySelector('.bk-viewall[href]');
        return panel && viewAll ? { label: tab.textContent.trim(), panel, href: viewAll.href } : null;
      }).filter(Boolean);
    }

    activateMobileTab(tab, tabs = this.mobileTabs || [], panels = this.mobileTabPanels || [], animate = true) {
      const panelId = tab?.getAttribute('aria-controls');
      const selectedPanel = panels.find((panel) => panel.id === panelId);
      if (!selectedPanel) return;
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel === selectedPanel;
        panel.hidden = !active;
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
        panel.classList.toggle('is-entering', active && animate && !this.reducedMotion());
      });
      if (animate && !this.reducedMotion()) requestAnimationFrame(() => selectedPanel.classList.remove('is-entering'));
      this.loadPanelImages(selectedPanel);
      tab.scrollIntoView?.({ behavior: this.reducedMotion() ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    }

    handleMobileTabKeydown(event, tab, tabs, panels, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = document.documentElement.dir === 'rtl';
      const delta = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + delta + tabs.length) % tabs.length;
      tabs[nextIndex].focus();
      this.activateMobileTab(tabs[nextIndex], tabs, panels);
    }

    resetMobileNavigation() {
      if (this.mobileTabs?.length) this.activateMobileTab(this.mobileTabs[0], this.mobileTabs, this.mobileTabPanels, false);
    }

    loadPanelImages(panel) {
      panel?.querySelectorAll('img[data-bk-menu-src]').forEach((image) => {
        if (image.dataset.bkMenuSrc) image.src = image.dataset.bkMenuSrc;
        image.removeAttribute('data-bk-menu-src');
      });
    }

    deferPanelImages(panel) {
      panel?.querySelectorAll('img').forEach((image) => {
        const source = image.dataset.bkMenuSrc || image.currentSrc || image.src;
        if (!source || source.startsWith('data:')) return;
        image.dataset.bkMenuSrc = source;
        const width = image.getAttribute('width') || 240;
        const height = image.getAttribute('height') || 240;
        image.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'/%3E`;
        image.removeAttribute('srcset');
      });
    }

    prepareMegaMenu() {
      if (!this.megaPanel) return;
      this.megaPanel.setAttribute('aria-hidden', 'true');
      if (!this.megaPanel.id) this.megaPanel.id = `BKV29Mega-${this.sectionId}`;

      const shopLabels = new Set(['shop', 'تسوق', 'تسوّق', 'تسوقي', 'تسوّقي']);
      const triggers = Array.from(this.header.querySelectorAll('.header__inline-menu > ul > li > a, .header__inline-menu .list-menu--inline > li > a, .header__inline-menu .list-menu--inline > li > .header__menu-item'));
      this.shopTriggers = triggers.filter((trigger) => shopLabels.has(normalize(trigger.textContent)));
      if (!this.shopTriggers.length) return;

      this.section.classList.add('bk-v29-mega-ready');
      const shopFallbackUrl = this.mobilePanel?.dataset.bkShopAllUrl
        || `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}collections/all`;
      this.shopTriggers.forEach((trigger) => {
        trigger.closest('li')?.classList.add('bk-v29-enhanced-shop');
        if (trigger.matches('a[href]')) trigger.setAttribute('href', shopFallbackUrl);
        trigger.setAttribute('aria-controls', this.megaPanel.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-haspopup', 'true');
        this.listen(trigger, 'pointerenter', (event) => {
          if (!this.collapsed && (event.pointerType === 'mouse' || event.pointerType === 'pen')) this.scheduleMegaOpen();
        });
        this.listen(trigger, 'pointerleave', () => this.scheduleMegaClose());
        this.listen(trigger, 'focusin', () => {
          if (!this.collapsed) this.openMega();
        });
        this.listen(trigger, 'pointerdown', () => this.pointerOpenState.set(trigger, this.megaOpen));
        this.listen(trigger, 'click', (event) => {
          if (this.collapsed) return;
          event.preventDefault();
          const startedOpen = this.pointerOpenState.get(trigger);
          this.pointerOpenState.delete(trigger);
          if (startedOpen === true) this.closeMega();
          else this.openMega();
        });
        this.listen(trigger, 'keydown', (event) => {
          if (this.collapsed || event.key !== ' ') return;
          event.preventDefault();
          this.megaOpen ? this.closeMega() : this.openMega();
        });
      });

      this.listen(this.megaPanel, 'pointerenter', () => this.cancelMegaClose());
      this.listen(this.megaPanel, 'pointerleave', () => this.scheduleMegaClose());
      this.listen(this.megaPanel, 'focusin', () => this.cancelMegaClose());
      this.prepareMegaTabs();
      this.prepareMegaCarousels();
    }

    prepareConcernMenu() {
      if (!this.concernPanel) return;
      const labels = new Set(['shop by concern', 'تسوقي حسب الاحتياج', 'تسوّقي حسب الاحتياج', 'تسوق حسب الاحتياج', 'تسوقي حسب احتياجك']);
      const triggers = Array.from(this.header.querySelectorAll('.header__inline-menu .list-menu--inline > li > a, .header__inline-menu > ul > li > a'));
      this.concernTriggers = triggers.filter((trigger) => labels.has(normalize(trigger.textContent)));
      if (!this.concernTriggers.length) return;
      if (!this.concernPanel.id) this.concernPanel.id = `BKV292Concern-${this.sectionId}`;
      const concernFallbackUrl = this.mobilePanel?.dataset.bkShopAllUrl
        || `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}collections/all`;
      this.concernTriggers.forEach((trigger) => {
        if (trigger.matches('a[href]')) trigger.setAttribute('href', concernFallbackUrl);
        trigger.setAttribute('aria-controls', this.concernPanel.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-haspopup', 'true');
        this.listen(trigger, 'pointerenter', (event) => {
          if (!this.collapsed && (event.pointerType === 'mouse' || event.pointerType === 'pen')) this.openConcern();
        });
        this.listen(trigger, 'pointerleave', () => this.scheduleConcernClose());
        this.listen(trigger, 'focusin', () => {
          if (!this.collapsed) this.openConcern();
        });
        this.listen(trigger, 'pointerdown', () => this.pointerOpenState.set(trigger, this.concernOpen));
        this.listen(trigger, 'click', (event) => {
          if (this.collapsed) return;
          event.preventDefault();
          const startedOpen = this.pointerOpenState.get(trigger);
          this.pointerOpenState.delete(trigger);
          if (startedOpen === true) this.closeConcern();
          else this.openConcern();
        });
        this.listen(trigger, 'keydown', (event) => {
          if (this.collapsed || event.key !== ' ') return;
          event.preventDefault();
          this.concernOpen ? this.closeConcern() : this.openConcern();
        });
      });
      this.listen(this.concernPanel, 'pointerenter', () => this.cancelConcernClose());
      this.listen(this.concernPanel, 'pointerleave', () => this.scheduleConcernClose());
      this.listen(this.concernPanel, 'focusin', () => this.cancelConcernClose());
      this.prepareConcernTabs();
    }

    prepareConcernTabs() {
      const tabs = Array.from(this.concernPanel?.querySelectorAll('[data-bk-concern-tab]') || []);
      const panels = Array.from(this.concernPanel?.querySelectorAll('[data-bk-concern-panel]') || []);
      if (!tabs.length || !panels.length) return;
      const activate = (tab) => this.activateTabPanel(tab, tabs, panels);
      tabs.forEach((tab, index) => {
        this.listen(tab, 'pointerenter', (event) => {
          if (event.pointerType === 'mouse' || event.pointerType === 'pen') activate(tab);
        });
        this.listen(tab, 'focus', () => activate(tab));
        this.listen(tab, 'click', (event) => {
          if (!this.collapsed) event.preventDefault();
          activate(tab);
        });
        this.listen(tab, 'keydown', (event) => this.handleTabKeydown(event, tab, tabs, activate, index));
      });
      activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    }

    openConcern() {
      if (this.collapsed || !this.concernPanel || !this.concernTriggers?.length || this.concernOpen) return;
      this.closeMega();
      this.cancelConcernClose();
      this.enterNavigationFlow();
      this.concernOpen = true;
      this.concernPanel.hidden = false;
      this.concernPanel.setAttribute('aria-hidden', 'false');
      this.concernTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      this.section.classList.add('bk-concern-open', 'bk-v29-shell-open');
    }

    closeConcern() {
      this.cancelConcernClose();
      if (!this.concernOpen) return;
      this.concernOpen = false;
      this.concernPanel.setAttribute('aria-hidden', 'true');
      this.concernTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.section.classList.remove('bk-concern-open', 'bk-v29-shell-open');
      this.concernPanel.hidden = true;
      this.leaveNavigationFlow();
    }

    scheduleConcernClose() {
      this.cancelConcernClose();
      this.concernCloseTimer = window.setTimeout(() => {
        const hoveringTrigger = this.concernTriggers?.some((trigger) => trigger.matches(':hover'));
        if (!hoveringTrigger && !this.concernPanel?.matches(':hover') && !this.concernPanel?.contains(document.activeElement)) this.closeConcern();
      }, 260);
    }

    cancelConcernClose() {
      window.clearTimeout(this.concernCloseTimer);
      this.concernCloseTimer = 0;
    }

    prepareMegaTabs() {
      const tabs = Array.from(this.megaPanel?.querySelectorAll('.bk-tabs [role="tab"]') || []);
      const panels = Array.from(this.megaPanel?.querySelectorAll('.bk-panel[role="tabpanel"], .bk-panel') || []);
      if (!tabs.length || !panels.length) return;

      const activate = (tab) => this.activateTabPanel(tab, tabs, panels);

      tabs.forEach((tab, index) => {
        this.listen(tab, 'pointerenter', (event) => {
          if (event.pointerType === 'mouse' || event.pointerType === 'pen') activate(tab);
        });
        this.listen(tab, 'focus', () => activate(tab));
        this.listen(tab, 'click', (event) => {
          if ((tab.getAttribute('href') || '').startsWith('#')) event.preventDefault();
          activate(tab);
        });
        this.listen(tab, 'keydown', (event) => this.handleTabKeydown(event, tab, tabs, activate, index));
      });

      activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    }

    activateTabPanel(tab, tabs, panels) {
      const id = tab?.getAttribute('aria-controls');
      const selectedPanel = id ? panels.find((panel) => panel.id === id) : null;
      if (!selectedPanel) return;
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel === selectedPanel;
        if (active) panel.setAttribute('data-active', 'true');
        else panel.removeAttribute('data-active');
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      if (selectedPanel.closest('[data-bk-concern-menu]')) this.updateConcernPanelLayout(selectedPanel);
      this.loadPanelImages(selectedPanel);
    }

    updateConcernPanelLayout(panel) {
      const scroller = panel?.querySelector('.bk-carousel');
      if (!scroller) return;
      const count = Math.min(scroller.querySelectorAll('.bk-card').length, 5);
      scroller.classList.remove('bk-concern-count--1', 'bk-concern-count--2', 'bk-concern-count--3', 'bk-concern-count--4', 'bk-concern-count--5');
      if (count) scroller.classList.add(`bk-concern-count--${count}`);
      panel.classList.toggle('bk-concern-complete', scroller.querySelectorAll('.bk-card').length <= count);
    }

    handleTabKeydown(event, tab, tabs, activate, index) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activate(tab);
        return;
      }
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = document.documentElement.dir === 'rtl';
      const direction = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
      const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + direction + tabs.length) % tabs.length;
      tabs[nextIndex].focus();
      activate(tabs[nextIndex]);
    }

    prepareMegaCarousels() {
      this.megaPanel?.querySelectorAll('.bk-carousel-wrapper').forEach((wrapper) => {
        const scroller = wrapper.querySelector('.bk-carousel');
        const previous = wrapper.querySelector('.bk-prev');
        const next = wrapper.querySelector('.bk-next');
        if (!scroller || !previous || !next) return;
        const update = () => {
          const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
          previous.disabled = Math.abs(scroller.scrollLeft) <= 2;
          next.disabled = Math.abs(scroller.scrollLeft) >= max - 3;
        };
        const move = (direction) => scroller.scrollBy({ left: direction * scroller.clientWidth * 0.8, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        this.listen(previous, 'click', () => move(-1));
        this.listen(next, 'click', () => move(1));
        this.listen(scroller, 'scroll', update, { passive: true });
        update();
      });
    }

    bindGlobalBehavior() {
      this.listen(document, 'keydown', (event) => {
        if (this.searchOpen && event.key === 'Tab') {
          this.trapSearchFocus(event);
          return;
        }
        if (this.mobileOpen && event.key === 'Tab') {
          this.trapMobileFocus(event);
          return;
        }
        if (event.key !== 'Escape') return;
        if (document.body.classList.contains('cart-sidebar-show') && !document.body.matches('.edit-cart-show,.term-condition-show,.has-warning')) {
          event.preventDefault();
          document.querySelector('[data-close-cart-sidebar]')?.click();
          this.section.querySelector('.qi-cart')?.focus({ preventScroll: true });
          return;
        }
        if (this.searchOpen) this.closeSearch();
        else if (this.mobileOpen) this.closeMobile();
        else if (this.concernOpen) {
          // Focus while open: its focusin handler must not reopen a just-closed menu.
          this.concernTriggers?.[0]?.focus({ preventScroll: true });
          this.closeConcern();
        }
        else if (this.megaOpen) {
          this.shopTriggers?.[0]?.focus({ preventScroll: true });
          this.closeMega();
        }
      });
      this.listen(document, 'pointerdown', (event) => {
        if (this.mobileOpen && !this.mobilePanel.contains(event.target) && !this.menuButton.contains(event.target)) this.closeMobile();
        if (this.megaOpen && !this.megaPanel.contains(event.target) && !this.shopTriggers?.some((trigger) => trigger.contains(event.target))) this.closeMega();
        if (this.concernOpen && !this.concernPanel.contains(event.target) && !this.concernTriggers?.some((trigger) => trigger.contains(event.target))) this.closeConcern();
      });
      this.listen(document, 'focusin', (event) => {
        if (this.megaOpen && !this.megaPanel.contains(event.target) && !this.shopTriggers?.some((trigger) => trigger.contains(event.target))) this.closeMega();
        if (this.concernOpen && !this.concernPanel.contains(event.target) && !this.concernTriggers?.some((trigger) => trigger.contains(event.target))) this.closeConcern();
      });
      this.listen(window, 'resize', () => this.scheduleMeasure(), { passive: true });
      this.listen(window, 'orientationchange', () => {
        if (this.mobileOpen) this.closeMobile({ restoreFocus: false });
        this.scheduleMeasure();
      }, { passive: true });
      this.listen(window, 'scroll', () => this.updatePanelPosition(), { passive: true });
    }

    trapMobileFocus(event) {
      if (!this.mobilePanel || !this.mobileOpen) return;
      const panelItems = Array.from(this.mobilePanel.querySelectorAll(focusableSelector)).filter((item) => {
        if (item.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(item);
        return item.tabIndex >= 0 && style.display !== 'none' && style.visibility !== 'hidden';
      });
      const focusables = [this.menuButton, ...panelItems];
      if (focusables.length < 2) return;
      const activeIndex = focusables.indexOf(document.activeElement);
      if (event.shiftKey && activeIndex === 0) {
        event.preventDefault();
        focusables[focusables.length - 1].focus({ preventScroll: true });
      } else if (event.shiftKey && activeIndex === 1) {
        event.preventDefault();
        this.menuButton.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeIndex === focusables.length - 1) {
        event.preventDefault();
        this.menuButton.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeIndex === 0) {
        event.preventDefault();
        focusables[1].focus({ preventScroll: true });
      } else if (activeIndex === -1) {
        event.preventDefault();
        focusables[1].focus({ preventScroll: true });
      }
    }

    scheduleMegaOpen() {
      this.cancelMegaClose();
      if (this.megaOpen || this.openTimer) return;
      this.openTimer = window.setTimeout(() => {
        this.openTimer = 0;
        this.openMega();
      }, this.atHomeTop() ? 220 : 60);
    }

    scheduleMegaClose() {
      this.cancelMegaClose();
      this.closeTimer = window.setTimeout(() => {
        const hoveringTrigger = this.shopTriggers?.some((trigger) => trigger.matches(':hover'));
        if (!hoveringTrigger && !this.megaPanel?.matches(':hover') && !this.megaPanel?.contains(document.activeElement)) this.closeMega();
      }, 260);
    }

    cancelMegaClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = 0;
    }

    atHomeTop() {
      return document.body.classList.contains('template-index') && this.section.classList.contains('is-top');
    }

    enterNavigationFlow() {
      if (!this.atHomeTop()) return;
      this.section.classList.remove('is-top');
      this.section.classList.add('bk-temp-top-open');
      this.temporarilyChangedHomeTop = true;
    }

    leaveNavigationFlow() {
      if (!this.temporarilyChangedHomeTop) return;
      if (document.body.classList.contains('template-index') && window.scrollY <= 2) this.section.classList.add('is-top');
      this.section.classList.remove('bk-temp-top-open');
      this.temporarilyChangedHomeTop = false;
    }

    openMega() {
      if (this.collapsed || !this.megaPanel || !this.shopTriggers?.length || this.megaOpen) return;
      window.clearTimeout(this.openTimer);
      this.openTimer = 0;
      this.cancelMegaClose();
      this.closeConcern();
      this.enterNavigationFlow();
      this.megaOpen = true;
      this.section.classList.add('bk-mega-arming', 'bk-mega-open', 'bk-v29-shell-open');
      this.megaPanel.setAttribute('aria-hidden', 'false');
      this.shopTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      this.updatePanelPosition();
    }

    closeMega() {
      window.clearTimeout(this.openTimer);
      this.openTimer = 0;
      this.cancelMegaClose();
      if (!this.megaOpen && !this.section.classList.contains('bk-mega-open')) return;
      this.megaOpen = false;
      this.section.classList.remove('bk-mega-open', 'bk-mega-arming', 'bk-v29-shell-open');
      this.megaPanel?.setAttribute('aria-hidden', 'true');
      this.shopTriggers?.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.leaveNavigationFlow();
    }

    openMobile() {
      if (!this.mobilePanel || this.mobileOpen) return;
      this.mobileScrollX = window.scrollX;
      this.mobileScrollY = window.scrollY;
      this.closeMega();
      this.closeConcern();
      this.enterNavigationFlow();
      this.mobileOpen = true;
      this.returnFocus = document.activeElement;
      this.mobilePanel.hidden = false;
      this.mobilePanel.dataset.open = 'true';
      this.mobilePanel.setAttribute('aria-hidden', 'false');
      this.menuButton.setAttribute('aria-expanded', 'true');
      this.menuButton.setAttribute('aria-label', documentLanguage.startsWith('ar') ? 'إغلاق القائمة' : 'Close menu');
      this.menuButton.dataset.open = 'true';
      this.section.classList.add('bk-v29-menu-open', 'bk-v29-shell-open');
      const restoreScroll = () => window.scrollTo({ left: this.mobileScrollX, top: this.mobileScrollY, behavior: 'auto' });
      requestAnimationFrame(() => {
        const focusTarget = this.mobileTabs?.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.mobilePanel.querySelector(focusableSelector) || this.mobilePanel;
        focusTarget.focus({ preventScroll: true });
        restoreScroll();
        requestAnimationFrame(restoreScroll);
      });
      window.clearTimeout(this.mobileScrollRestoreTimer);
      this.mobileScrollRestoreTimer = window.setTimeout(restoreScroll, 260);
    }

    closeMobile({ restoreFocus = true } = {}) {
      if (!this.mobilePanel || !this.mobileOpen) return;
      this.mobileOpen = false;
      delete this.mobilePanel.dataset.open;
      this.mobilePanel.setAttribute('aria-hidden', 'true');
      this.menuButton.setAttribute('aria-expanded', 'false');
      this.menuButton.setAttribute('aria-label', documentLanguage.startsWith('ar') ? 'القائمة' : 'Menu');
      this.menuButton.removeAttribute('data-open');
      this.section.classList.remove('bk-v29-menu-open', 'bk-v29-shell-open');
      const restoreScroll = () => window.scrollTo({ left: this.mobileScrollX, top: this.mobileScrollY, behavior: 'auto' });
      restoreScroll();
      const finish = () => {
        if (this.mobileOpen) return;
        this.resetMobileNavigation();
        this.mobilePanel.hidden = true;
        this.leaveNavigationFlow();
        if (restoreFocus && this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus({ preventScroll: true });
        restoreScroll();
        this.returnFocus = null;
      };
      window.clearTimeout(this.mobileScrollRestoreTimer);
      this.reducedMotion() ? finish() : window.setTimeout(finish, 240);
    }

    scheduleMeasure() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.measure());
    }

    collides() {
      const left = this.left.getBoundingClientRect();
      const logo = this.logo.getBoundingClientRect();
      const right = this.right.getBoundingClientRect();
      const gap = 14;
      return left.right + gap > logo.left
        || right.left - gap < logo.right
        || this.left.scrollWidth > this.left.clientWidth + 1
        || this.right.scrollWidth > this.right.clientWidth + 1;
    }

    measure() {
      const previousCollapsed = this.collapsed;
      this.section.classList.remove('bk-v29-header-compact', 'bk-v29-header-collapsed');
      const viewportWidth = document.documentElement.clientWidth;
      const needsCollapse = viewportWidth <= 990;
      const needsCompact = !needsCollapse && viewportWidth <= 1280;
      if (needsCompact) this.section.classList.add('bk-v29-header-compact');
      this.section.classList.toggle('bk-v29-header-collapsed', needsCollapse);
      this.collapsed = needsCollapse;
      this.updatePanelPosition();

      if (needsCollapse) {
        this.closeMega();
        this.closeConcern();
      }
      if (!needsCollapse && this.mobileOpen) this.closeMobile();
      if (previousCollapsed !== needsCollapse) this.section.dispatchEvent(new CustomEvent('bk:navigation-mode', { detail: { collapsed: needsCollapse } }));
      this.concernPanel?.querySelectorAll('[data-bk-concern-panel][data-active="true"]').forEach((panel) => this.updateConcernPanelLayout(panel));
    }

    updatePanelPosition() {
      const rect = this.header.getBoundingClientRect();
      const top = Math.max(0, Math.round(rect.bottom));
      document.documentElement.style.setProperty('--bk-v29-header-height', `${Math.max(72, Math.round(rect.height))}px`);
      this.section.style.setProperty('--bk-v29-menu-top', `${top}px`);
      this.section.style.setProperty('--bk-v29-menu-max-height', `${Math.max(180, window.innerHeight - top)}px`);
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    destroy() {
      this.closeMega();
      this.closeConcern();
      this.closeSearch({ restoreFocus: false });
      this.closeMobile({ restoreFocus: false });
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.concernCloseTimer);
      window.clearTimeout(this.mobileScrollRestoreTimer);
      this.section?.classList.remove('bk-v29-controller-ready', 'bk-v29-mega-ready', 'bk-v29-menu-open', 'bk-v29-shell-open', 'bk-concern-open');
    }
  }

  function scan(scope = document) {
    const headers = scope.matches?.('[data-bk-v29-header]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-bk-v29-header]') || []);
    headers.forEach((header) => {
      const key = header.dataset.sectionId || header.closest('.section-header-navigation')?.id || 'header';
      const existing = controllers.get(key);
      if (existing?.header === header) return;
      existing?.destroy();
      controllers.set(key, new NavigationController(header));
    });
  }

  function unload(scope) {
    controllers.forEach((controller, key) => {
      if (scope.contains(controller.header)) {
        controller.destroy();
        controllers.delete(key);
      }
    });
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  window.BKV291Navigation = { scan };
  window.BKV29Header = window.BKV291Navigation;
  scan();
  normalizeInternalStorefrontLinks(document);
})();
